import { useState } from "react";
import CrudTable from "@/components/CrudTable";
import { MODULES } from "@/config/modules";
import { useLookups } from "@/hooks/useLookups";
import { fmtCurrency } from "@/lib/format";
import { TrendUp, Coins } from "@phosphor-icons/react";

export default function Finance() {
  const { lists, nameMap } = useLookups();
  const [rows, setRows] = useState([]);

  const revenue = rows.reduce((s, r) => s + Number(r.revenue || 0), 0);
  const expenses = rows.reduce((s, r) => s + Number(r.expenses || 0) + Number(r.marketing_spend || 0), 0);
  const profit = rows.reduce((s, r) => s + Number(r.profit || 0), 0);

  // Aging buckets based on entry date vs outstanding
  const today = new Date();
  const buckets = { "0-15": 0, "16-30": 0, "30+": 0 };
  rows.forEach((r) => {
    const out = Number(r.outstanding_payments || 0);
    if (!out) return;
    const days = Math.floor((today - new Date(r.date)) / 86400000);
    if (days <= 15) buckets["0-15"] += out;
    else if (days <= 30) buckets["16-30"] += out;
    else buckets["30+"] += out;
  });

  const cards = [
    { label: "Total Revenue", value: revenue, icon: TrendUp, color: "text-emerald", bg: "bg-emerald/10" },
    { label: "Total Costs", value: expenses, icon: Coins, color: "text-rose", bg: "bg-rose/10" },
    { label: "Net Profit", value: profit, icon: TrendUp, color: "text-navy", bg: "bg-gold/10" },
  ];

  const header = (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="rounded-lg bg-white border border-slate-200 p-4 flex items-center gap-3">
              <div className={`h-11 w-11 rounded-lg ${c.bg} flex items-center justify-center`}><Icon size={22} weight="fill" className={c.color} /></div>
              <div>
                <div className={`font-heading text-2xl font-extrabold ${c.color}`}>{fmtCurrency(c.value)}</div>
                <div className="text-xs text-slate">{c.label}</div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="rounded-lg bg-white border border-slate-200 p-5">
        <h3 className="font-heading font-bold text-navy mb-4">Outstanding Payments — Aging</h3>
        <div className="grid grid-cols-3 gap-4">
          {Object.entries(buckets).map(([label, val], i) => (
            <div key={label} className="text-center rounded-lg border border-slate-200 p-4">
              <div className={`font-heading text-2xl font-extrabold ${i === 2 ? "text-rose" : i === 1 ? "text-gold" : "text-navy"}`}>{fmtCurrency(val)}</div>
              <div className="text-xs text-slate mt-1">{label} days</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div data-testid="finance-page">
      <CrudTable resource="finance" config={MODULES.finance} lists={lists} nameMap={nameMap} onData={setRows} extraHeader={header} />
    </div>
  );
}
